import QueryString from "query-string";
import { all, call, put, select, takeEvery } from "redux-saga/effects";
import api from "../../utils/api";
import { errorAlert } from "../Alerts/actions";
import {
  exportAllocationFailure,
  exportAllocationSuccess,
  exportPayrollFailure,
  exportPayrollSuccess,
  exportTimesheetFailure,
  exportTimesheetSuccess,
  fetchAllSheetFailure,
  fetchAllSheetSuccess,
  fetchAllShiftFailure,
  fetchAllShiftSuccess,
  fetchBreaksFailure,
  fetchBreaksSuccess,
  fetchEmpsFailure,
  fetchEmpsSuccess,
  fetchSheetFailure,
  fetchSheetSuccess,
  fetchStatsFailure,
  fetchStatsSuccess,
  getPayrollFailure,
  getPayrollSuccess,
  getRangeFailure,
  getRangeSuccess,
  getReportFailure,
  getReportSuccess,
  getSheetHistoryFailure,
  getSheetHistorySuccess,
  lockPayrollFailure,
  lockPayrollSuccess,
  lockTimesheetFailure,
  lockTimesheetSuccess,
  setAccrual,
  setApprovalFailure,
  setApprovalSuccess,
  setClockFailure,
  setClockSuccess,
  setCurrentTimesheet,
  setDefAccrual,
  setDefPaid,
  setPaid,
  setWeekStats,
  toggleLock,
  toggleLockPayroll,
  updateAccrualFailure,
  updateAccrualSuccess,
  updateClockFailure,
  updateClockSuccess,
  updatePaidHoursFailure,
  updatePaidHoursSuccess,
} from "./actions";
import {
  EXPORT_ALLOCATION,
  EXPORT_PAYROLL,
  EXPORT_TIMESHEET,
  FETCH_ALL_SHEET,
  FETCH_ALL_SHIFT,
  FETCH_BREAKS,
  FETCH_EMPS,
  FETCH_SHEET,
  FETCH_STATS,
  GET_ALLOCATION_REPORT,
  GET_PAYROLL,
  GET_RANGE,
  GET_SHEET_HISTORY,
  LOCK_PAYROLL,
  LOCK_TIMESHEET,
  SET_APPROVAL,
  SET_CLOCK,
  UPDATE_ACCRUAL,
  UPDATE_CLOCK,
  UPDATE_PAID_HOURS,
} from "./constants";

export function* fetchSheetSaga({
  payload = { cancelToken: null },
  onSuccess,
}) {
  const token = yield select((state) => state?.AuthReducer?.token);
  let query = payload ? QueryString.stringify(payload) : "";
  try {
    const { data } = yield call(
      api(token).get,
      `/api/attendance?${query}`,
      payload,
    );
    yield put(
      fetchSheetSuccess({
        data,
      }),
    );
    yield put(toggleLock(data?.is_locked));
    typeof onSuccess === "function" && onSuccess(data);
    yield put(setCurrentTimesheet(data?.timesheet_details ?? {}));
  } catch (e) {
    yield put(fetchSheetFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while fetching sheet!"));
  }
}

export function* setClockSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(api(token).get, `/api/attendance`, payload);
    yield put(setClockSuccess(data));
  } catch (e) {
    yield put(setClockFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while updating data!"));
  }
}

export function* updateClockSaga({ payload, onUpdate }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).post,
      `/api/attendance-in-out`,
      payload,
    );
    if (data?.id && data?.total_paid_hours) {
      yield put(fetchStatsSuccess(data?.total_paid_hours));
    }
    yield put(updateClockSuccess(data));
    typeof onUpdate === "function" && onUpdate(data);
  } catch (e) {
    yield put(updateClockFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while updating data!"));
  }
}

export function* setApprovalSaga({ payload, onApproval }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).post,
      `/api/approved-unapproved/${payload.id}`,
      payload.data,
    );
    yield put(setApprovalSuccess(data));
    typeof onApproval === "function" &&
      onApproval({
        data: { ...payload.data, id: payload.id },
        helpers: payload.helpers,
      });
  } catch (e) {
    yield put(setApprovalFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while updating data!"));
  }
}

export function* exportTimesheetSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const data = yield call(
      api(token, null, false, true).post,
      `/api/export-timesheet`,
      payload.params,
    );
    if (data) {
      yield put(exportTimesheetSuccess());
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${payload.title}.xlsx`);
      document.body.appendChild(link);
      link.click();
      typeof onSuccess === "function" && onSuccess();
    }
  } catch (e) {
    yield put(exportTimesheetFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while generating report!"));
    else yield put(errorAlert("Error while generating report!"));
  }
}

export function* fetchAllSheetSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).post,
      `/api/employee-timesheet`,
      payload,
    );
    yield put(fetchAllSheetSuccess(data));
  } catch (e) {
    yield put(fetchAllSheetFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while fetching employee timesheet!"));
  }
}

export function* fetchAllShiftSaga({ payload, onClockFetch }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { allSheet } = yield select((state) => state?.SheetReducer);
  try {
    const { data } = yield call(
      api(token).post,
      `/api/employee-clock-timesheet`,
      payload,
    );
    yield put(fetchAllShiftSuccess(data));
    typeof onClockFetch === "function" &&
      onClockFetch({
        old: allSheet,
        new: data,
      });
  } catch (e) {
    yield put(fetchAllShiftFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while timehsheet shifts!"));
  }
}

export function* getReportSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(api(token).post, `/api/role-budget`, payload);
    yield put(getReportSuccess(data));
  } catch (e) {
    yield put(getReportFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while generating role budget report!"));
  }
}

export function* lockTimesheetSaga({ payload, onLock }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).post,
      `/api/lock-timesheet`,
      payload,
    );
    yield put(lockTimesheetSuccess());
    yield put(toggleLock(data?.is_locked));
    typeof onLock === "function" && onLock(data);
    yield put(setCurrentTimesheet(data?.timesheet_details ?? {}));
  } catch (e) {
    yield put(lockTimesheetFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while locking timesheet"));
  }
}

export function* getSheetHistorySaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).get,
      `/api/timesheet-history/${payload?.id}`,
    );
    yield put(getSheetHistorySuccess(data));
  } catch (e) {
    yield put(getSheetHistoryFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while fetching sheet history!"));
  }
}

export function* updateAccrualSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const weekStats = yield select((state) => state?.SheetReducer?.weekStats);
  try {
    const { data } = yield call(
      api(token).post,
      `/api/accrued_leave-update/${payload.id}`,
      payload.data,
    );
    yield put(updateAccrualSuccess());
    yield put(
      setWeekStats({
        ...weekStats,
        accrued_leave: payload.data.accrued_leave,
        paid_hours: payload.data.paid_hours,
      }),
    );
    yield put(setAccrual(payload?.data?.accrued_leave ?? ""));
    yield put(setDefAccrual(payload?.data?.accrued_leave ?? ""));
    yield put(setPaid(payload?.data?.paid_hours ?? ""));
    yield put(setDefPaid(payload?.data?.paid_hours ?? ""));
  } catch (e) {
    yield put(updateAccrualFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while updating data!"));
  }
}

export function* getRangeSaga({ onRangeSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(api(token).get, `/api/timesheet-periods-list`);
    yield put(getRangeSuccess());
    typeof onRangeSuccess === "function" && onRangeSuccess(data);
  } catch (e) {
    yield put(getRangeFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while fetching range!"));
  }
}

export function* updatePaidHoursSaga({ payload, onUpdate }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).post,
      `/api/save-holiday-paid-hours/${payload.id}`,
      payload.data,
    );
    if (data?.id && data?.total_paid_hours) {
      yield put(fetchStatsSuccess(data?.total_paid_hours));
      typeof onUpdate === "function" && onUpdate(data);
    }
    yield put(updatePaidHoursSuccess());
  } catch (e) {
    yield put(updatePaidHoursFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while updating data!"));
  }
}

export function* fetchEmpsSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  let query = payload ? QueryString.stringify(payload) : "";
  try {
    const { data } = yield call(
      api(token).get,
      `/api/get-timesheet-employee-list?${query}`,
    );
    yield put(fetchEmpsSuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(fetchEmpsFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while fetching employees!"));
  }
}

export function* fetchStatsSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  let query = payload ? QueryString.stringify(payload) : "";
  try {
    const { data } = yield call(
      api(token).get,
      `/api/get-employee-total-paid-hours?${query}`,
    );
    yield put(fetchStatsSuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(fetchStatsFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while fetching stats!"));
  }
}

export function* getPayrollSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).post,
      `/api/payroll-emp-total`,
      payload,
    );
    yield put(getPayrollSuccess(data));
    yield put(toggleLockPayroll(data?.is_payroll_finalised ?? false));
  } catch (e) {
    yield put(getPayrollFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while fetching stats!"));
  }
}

export function* lockPayrollSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).post,
      `/api/save-payroll-finalised`,
      payload,
    );
    yield put(lockPayrollSuccess(data));
  } catch (e) {
    yield put(lockPayrollFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while fetching stats!"));
  }
}

export function* exportPayrollSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { type, params } = payload;
  const apiConfig = {
    generic: `/api/export-payroll`,
    break: `/api/export-payroll-break-down`,
    location: `/api/export-payroll-location-break-down`,
    moneysoft: `/api/moneysoft-payroll-export`,
  };
  try {
    const data = yield call(
      api(token, null, false, true).post,
      apiConfig[type],
      params,
    );
    if (data) {
      yield put(exportPayrollSuccess({ isFetching: false }));
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${payload.title}.csv`);
      document.body.appendChild(link);
      link.click();
      typeof onSuccess === "function" && onSuccess();
    }
  } catch (e) {
    yield put(exportPayrollFailure({ isFetching: false }));
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while generating payroll report!"));
    else yield put(errorAlert("Error while generating payroll report!"));
  }
}

export function* fetchBreaksSaga({ payload, onSuccess }) {
  const { clockId } = payload;
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).get,
      `/api/clock-break-list/${clockId}`,
    );
    yield put(fetchBreaksSuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(fetchBreaksFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while fetching list!"));
  }
}

export function* exportAllocationSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const data = yield call(
      api(token, null, false, true).post,
      `/api/export-allocation-report`,
      payload,
    );
    if (data) {
      yield put(exportAllocationSuccess({ isFetching: false }));
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Allocation_${payload.from_date}_${payload.to_date}.csv`,
      );
      document.body.appendChild(link);
      link.click();
    }
  } catch (e) {
    yield put(exportAllocationFailure({ isFetching: false }));
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while generating allocation report!"));
    else yield put(errorAlert("Error while generating allocation report!"));
  }
}

export function* fetchSheetFlow() {
  yield takeEvery(FETCH_SHEET, fetchSheetSaga);
}
export function* setClockFlow() {
  yield takeEvery(SET_CLOCK, setClockSaga);
}
export function* updateClockFlow() {
  yield takeEvery(UPDATE_CLOCK, updateClockSaga);
}
export function* setApprovalFlow() {
  yield takeEvery(SET_APPROVAL, setApprovalSaga);
}
export function* exportTimesheetFlow() {
  yield takeEvery(EXPORT_TIMESHEET, exportTimesheetSaga);
}
export function* fetchAllSheetFlow() {
  yield takeEvery(FETCH_ALL_SHEET, fetchAllSheetSaga);
}
export function* fetchAllShifFlow() {
  yield takeEvery(FETCH_ALL_SHIFT, fetchAllShiftSaga);
}
export function* getReportFlow() {
  yield takeEvery(GET_ALLOCATION_REPORT, getReportSaga);
}
export function* lockTimesheetFlow() {
  yield takeEvery(LOCK_TIMESHEET, lockTimesheetSaga);
}
export function* getSheetHistoryFlow() {
  yield takeEvery(GET_SHEET_HISTORY, getSheetHistorySaga);
}
export function* updateAccrualFlow() {
  yield takeEvery(UPDATE_ACCRUAL, updateAccrualSaga);
}
export function* getRangeFlow() {
  yield takeEvery(GET_RANGE, getRangeSaga);
}
export function* updatePaidHoursFlow() {
  yield takeEvery(UPDATE_PAID_HOURS, updatePaidHoursSaga);
}
export function* fetchEmpsFlow() {
  yield takeEvery(FETCH_EMPS, fetchEmpsSaga);
}
export function* fetchStatsFlow() {
  yield takeEvery(FETCH_STATS, fetchStatsSaga);
}
export function* getPayrollFlow() {
  yield takeEvery(GET_PAYROLL, getPayrollSaga);
}
export function* lockPayrollFlow() {
  yield takeEvery(LOCK_PAYROLL, lockPayrollSaga);
}
export function* exportPayrollFlow() {
  yield takeEvery(EXPORT_PAYROLL, exportPayrollSaga);
}
export function* fetchBreaksFlow() {
  yield takeEvery(FETCH_BREAKS, fetchBreaksSaga);
}
export function* exportAllocationFlow() {
  yield takeEvery(EXPORT_ALLOCATION, exportAllocationSaga);
}

export default function* SheetSaga() {
  yield all([
    fetchSheetFlow(),
    setClockFlow(),
    updateClockFlow(),
    setApprovalFlow(),
    exportTimesheetFlow(),
    fetchAllSheetFlow(),
    fetchAllShifFlow(),
    getReportFlow(),
    lockTimesheetFlow(),
    getSheetHistoryFlow(),
    updateAccrualFlow(),
    getRangeFlow(),
    updatePaidHoursFlow(),
    fetchEmpsFlow(),
    fetchStatsFlow(),
    getPayrollFlow(),
    lockPayrollFlow(),
    exportPayrollFlow(),
    fetchBreaksFlow(),
    exportAllocationFlow(),
  ]);
}
